import { PROTOCOL_VERSION } from '../../core/protocols';
import { createSeededRandom } from '../../core/random';
import { clampDifficulty } from '../../core/staircase';
import type { SessionMode, TrialPlan, TrialResult } from '../../core/types';
import type {
  ModuleFeedback,
  ResponseOption,
  ResponseVisual,
  RuntimeTrainingModule,
  TrainingFrameContext,
} from '../runtime';
import { estimateFixedFormThreshold } from './fixed-threshold';
import { estimateReversalThreshold } from './reversal-threshold';
import { getVehicleImage, type CentralVehicleType } from './vehicle-assets';
import { adaptDifficultyToPerformance } from '../adaptive-difficulty';

export type { CentralVehicleType } from './vehicle-assets';
/** Legacy balancing axis retained for deterministic fixed-form queues. */
export type CentralSymbol = 'vertical' | 'horizontal';
export type PeripheralPosition = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
export type RoadSignSymbol = 'circle' | 'cross';
export const CENTRAL_PERIPHERAL_ECCENTRICITIES = [4.5, 8] as const;

interface VisualResponseOption extends ResponseOption {
  visual?: ResponseVisual;
  position?: { xPercent: number; yPercent: number };
}

export interface PeripheralDistractor {
  position: PeripheralPosition;
  symbol: 'cross';
}

export interface CentralPeripheralStimulus {
  centralVehicle: CentralVehicleType;
  /** Vehicle-choice A/B balance; this is not rendered as a line symbol. */
  centralSymbol: CentralSymbol;
  vehicleChoices: readonly [CentralVehicleType, CentralVehicleType];
  vehicleSimilarity: number;
  peripheralPosition: PeripheralPosition;
  targetSignSymbol: 'circle';
  eccentricityDegrees: number;
  presentationMs: number;
  maskMs: 260;
  backgroundComplexity: number;
  distractorCount: number;
  distractors: PeripheralDistractor[];
}

const POSITION_LABELS = ['上', '右上', '右', '右下', '下', '左下', '左', '左上'] as const;

const VEHICLE_LABELS: Record<CentralVehicleType, string> = {
  convertible: 'オープンカー',
  'box-truck': 'ボックス車',
  sedan: 'セダン',
  suv: 'SUV',
  hatchback: 'ハッチバック',
};

export function difficultyToPeripheralDurationMs(difficulty: number): number {
  // Protocol 7.1 halves every level of the previous 1,450–725 ms curve.
  return Math.round((580 - (clampDifficulty(difficulty, 1, 5) - 1) * 72.5) * 1.25);
}

function vehiclePairForDifficulty(
  difficulty: number,
): readonly [CentralVehicleType, CentralVehicleType] {
  if (difficulty < 2.25) return ['convertible', 'box-truck'];
  if (difficulty < 3.75) return ['sedan', 'suv'];
  return ['sedan', 'hatchback'];
}

export function peripheralPositionDegrees(
  stimulus: Pick<CentralPeripheralStimulus, 'peripheralPosition' | 'eccentricityDegrees'>,
): { xDegrees: number; yDegrees: number } {
  const angle = -Math.PI / 2 + ((stimulus.peripheralPosition - 1) * Math.PI) / 4;
  return {
    xDegrees: Math.cos(angle) * stimulus.eccentricityDegrees,
    yDegrees: Math.sin(angle) * stimulus.eccentricityDegrees,
  };
}

export function peripheralPositionPercent(position: PeripheralPosition): {
  xPercent: number;
  yPercent: number;
} {
  const angle = -Math.PI / 2 + ((position - 1) * Math.PI) / 4;
  return {
    xPercent: Number((50 + Math.cos(angle) * 34).toFixed(3)),
    yPercent: Number((50 + Math.sin(angle) * 40).toFixed(3)),
  };
}

export function peripheralMarkerRadiusPixels(pixelsPerDegree: number): number {
  return Math.max(8, pixelsPerDegree * 0.24);
}

export function centralVehicleSizePixels(pixelsPerDegree: number): {
  width: number;
  height: number;
} {
  const width = Math.max(72, Math.min(104, pixelsPerDegree * 2.26));
  return { width, height: width * 0.5 };
}

export function createCentralPeripheralStimulus(
  seed: number,
  difficulty: number,
): CentralPeripheralStimulus {
  const random = createSeededRandom(seed);
  const bounded = clampDifficulty(difficulty, 1, 5);
  const progress = (bounded - 1) / 4;
  const vehicleChoices = vehiclePairForDifficulty(bounded);
  const peripheralPosition = random.integer(1, 8) as PeripheralPosition;
  const distractorCount = Math.min(7, 1 + Math.round(progress * 6));
  const remainingPositions = random
    .shuffle(Array.from({ length: 8 }, (_, index) => (index + 1) as PeripheralPosition))
    .filter((position) => position !== peripheralPosition)
    .slice(0, distractorCount);
  const centralVehicle = random.pick(vehicleChoices);

  return {
    centralVehicle,
    centralSymbol: centralVehicle === vehicleChoices[0] ? 'vertical' : 'horizontal',
    vehicleChoices,
    vehicleSimilarity: Number((0.08 + progress * 0.84).toFixed(4)),
    peripheralPosition,
    targetSignSymbol: 'circle',
    eccentricityDegrees: Number((4.5 + progress * 3.5).toFixed(3)),
    presentationMs: difficultyToPeripheralDurationMs(bounded),
    maskMs: 260,
    backgroundComplexity: Number((0.1 + progress * 0.9).toFixed(4)),
    distractorCount,
    distractors: remainingPositions.map((position) => ({ position, symbol: 'cross' })),
  };
}

function drawLandscape(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  seed: number,
  complexity: number,
): void {
  const random = createSeededRandom(seed ^ 0x2e75_a819);
  const horizon = height * 0.46;
  const sky = context.createLinearGradient(0, 0, 0, horizon);
  sky.addColorStop(0, '#87b8ca');
  sky.addColorStop(0.68, '#cbd7ce');
  sky.addColorStop(1, '#e7d5b7');
  context.fillStyle = sky;
  context.fillRect(0, 0, width, horizon);

  context.fillStyle = 'rgba(255, 239, 195, 0.72)';
  context.beginPath();
  context.arc(width * 0.82, height * 0.14, height * 0.065, 0, Math.PI * 2);
  context.fill();

  const cloudCount = 2 + Math.round(complexity * 7);
  for (let index = 0; index < cloudCount; index += 1) {
    const x = random.range(-0.05, 1.05) * width;
    const y = random.range(0.07, 0.34) * height;
    const scale = random.range(0.5, 1.35) * (26 + complexity * 10);
    context.fillStyle = `rgba(239, 242, 229, ${0.28 + random.range(0, 0.24)})`;
    context.beginPath();
    context.ellipse(x, y, scale, scale * 0.34, 0, 0, Math.PI * 2);
    context.ellipse(x + scale * 0.38, y + 1, scale * 0.58, scale * 0.3, 0, 0, Math.PI * 2);
    context.fill();
  }

  const mountainLayers = 2 + Math.round(complexity * 2);
  for (let layer = 0; layer < mountainLayers; layer += 1) {
    const base = horizon + layer * height * 0.026;
    context.fillStyle = layer === 0 ? '#738c78' : layer === 1 ? '#57715e' : '#455e4c';
    context.beginPath();
    context.moveTo(0, base);
    const points = 8 + Math.round(complexity * 8);
    for (let point = 0; point <= points; point += 1) {
      const x = (point / points) * width;
      const peak = random.range(0.035, 0.14) * height * (1 - layer * 0.13);
      context.lineTo(x, base - peak);
    }
    context.lineTo(width, horizon + height * 0.18);
    context.lineTo(0, horizon + height * 0.18);
    context.closePath();
    context.fill();
  }

  context.fillStyle = '#756c55';
  context.fillRect(0, horizon, width, height - horizon);
  context.fillStyle = '#383d3b';
  context.beginPath();
  context.moveTo(width * 0.43, horizon);
  context.lineTo(width * 0.57, horizon);
  context.lineTo(width * 0.93, height);
  context.lineTo(width * 0.07, height);
  context.closePath();
  context.fill();
  context.strokeStyle = '#e7e5cf';
  context.lineWidth = 3;
  context.beginPath();
  context.moveTo(width * 0.43, horizon);
  context.lineTo(width * 0.07, height);
  context.moveTo(width * 0.57, horizon);
  context.lineTo(width * 0.93, height);
  context.stroke();

  const dashCount = 7 + Math.round(complexity * 5);
  for (let index = 0; index < dashCount; index += 1) {
    const depth = (index + 0.25) / dashCount;
    const curved = depth * depth;
    const y = horizon + curved * (height - horizon);
    const dashHeight = 3 + curved * 34;
    context.fillStyle = 'rgba(244, 232, 188, 0.78)';
    context.fillRect(width / 2 - 2 - curved * 4, y, 4 + curved * 8, dashHeight);
  }

  const roadsideCount = 4 + Math.round(complexity * 22);
  for (let index = 0; index < roadsideCount; index += 1) {
    const right = random.boolean();
    const x = right ? random.range(0.71, 1.02) * width : random.range(-0.02, 0.29) * width;
    const groundY = random.range(horizon * 0.89, height * 0.9);
    const size = random.range(8, 18 + complexity * 18) * (0.45 + (groundY / height) * 0.8);
    if (random.next() < 0.62) {
      context.fillStyle = random.pick(['#294932', '#365b39', '#476c40', '#53633c']);
      context.beginPath();
      context.moveTo(x, groundY - size * 1.9);
      context.lineTo(x + size, groundY);
      context.lineTo(x - size, groundY);
      context.closePath();
      context.fill();
      context.fillStyle = '#4f4030';
      context.fillRect(x - 2, groundY - 2, 4, size * 0.75);
    } else {
      context.fillStyle = random.pick(['#9b785d', '#8a8d7c', '#b19a78']);
      context.fillRect(x - size * 0.7, groundY - size * 1.15, size * 1.4, size * 1.15);
      context.fillStyle = '#453c35';
      context.beginPath();
      context.moveTo(x - size * 0.85, groundY - size * 1.15);
      context.lineTo(x, groundY - size * 1.75);
      context.lineTo(x + size * 0.85, groundY - size * 1.15);
      context.closePath();
      context.fill();
      if (complexity > 0.45) {
        context.fillStyle = '#b8d1c8';
        context.fillRect(x - size * 0.36, groundY - size * 0.92, size * 0.27, size * 0.28);
        context.fillRect(x + size * 0.1, groundY - size * 0.92, size * 0.27, size * 0.28);
      }
    }
  }
}

function drawVehicle(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  type: CentralVehicleType,
): void {
  const image = getVehicleImage(type);
  if (!image?.complete || image.naturalWidth === 0) return;
  context.save();
  context.shadowColor = 'rgba(241, 236, 204, 0.3)';
  context.shadowBlur = Math.max(1, width * 0.035);
  context.drawImage(image, x - width / 2, y - height / 2, width, height);
  context.restore();
}

function drawRoadSign(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
  symbol: RoadSignSymbol,
): void {
  context.save();
  context.translate(x, y);

  context.strokeStyle = '#4d4b42';
  context.lineWidth = Math.max(2, radius * 0.13);
  context.beginPath();
  context.moveTo(0, radius * 0.9);
  context.lineTo(0, radius * 2.35);
  context.stroke();

  context.fillStyle = '#ffffff';
  context.strokeStyle = '#202725';
  context.lineWidth = Math.max(1.5, radius * 0.11);
  context.beginPath();
  context.roundRect(-radius, -radius, radius * 2, radius * 2, radius * 0.2);
  context.fill();
  context.stroke();

  const glyphRadius = radius * 0.56;
  context.fillStyle = '#000000';
  context.strokeStyle = '#000000';
  switch (symbol) {
    case 'circle':
      context.beginPath();
      context.arc(0, 0, glyphRadius, 0, Math.PI * 2);
      context.lineWidth = Math.max(2.5, radius * 0.22);
      context.stroke();
      break;
    case 'cross':
      context.lineWidth = Math.max(3, radius * 0.3);
      context.lineCap = 'round';
      context.beginPath();
      context.moveTo(-glyphRadius * 0.78, -glyphRadius * 0.78);
      context.lineTo(glyphRadius * 0.78, glyphRadius * 0.78);
      context.moveTo(glyphRadius * 0.78, -glyphRadius * 0.78);
      context.lineTo(-glyphRadius * 0.78, glyphRadius * 0.78);
      context.stroke();
      break;
  }
  context.restore();
}

function renderStaticMask(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  seed: number,
): void {
  const random = createSeededRandom(seed ^ 0x6f4a_1c2d);
  const cell = 10;
  for (let y = 0; y < height; y += cell) {
    for (let x = 0; x < width; x += cell) {
      const shade = random.integer(52, 204);
      const tint = random.integer(-10, 10);
      context.fillStyle = `rgb(${shade + tint}, ${shade}, ${shade - tint})`;
      context.fillRect(x, y, cell, cell);
    }
  }
}

function renderCentralPeripheral(
  context: CanvasRenderingContext2D,
  frame: TrainingFrameContext,
  trial: TrialPlan<CentralPeripheralStimulus>,
): void {
  context.save();
  context.setTransform(frame.devicePixelRatio, 0, 0, frame.devicePixelRatio, 0, 0);
  const stimulus = trial.stimulus;
  if (frame.elapsed < stimulus.presentationMs) {
    drawLandscape(context, frame.width, frame.height, trial.seed, stimulus.backgroundComplexity);
    const centerX = frame.width / 2;
    const centerY = frame.height / 2;
    const vehicleSize = centralVehicleSizePixels(frame.pixelsPerDegree);
    drawVehicle(context, centerX, centerY, vehicleSize.width, vehicleSize.height, stimulus.centralVehicle);

    const markerRadius = peripheralMarkerRadiusPixels(frame.pixelsPerDegree);
    const targetPosition = peripheralPositionDegrees(stimulus);
    drawRoadSign(
      context,
      centerX + targetPosition.xDegrees * frame.pixelsPerDegree,
      centerY + targetPosition.yDegrees * frame.pixelsPerDegree,
      markerRadius,
      stimulus.targetSignSymbol,
    );
    for (const distractor of stimulus.distractors) {
      const position = peripheralPositionDegrees({
        peripheralPosition: distractor.position,
        eccentricityDegrees: stimulus.eccentricityDegrees,
      });
      drawRoadSign(
        context,
        centerX + position.xDegrees * frame.pixelsPerDegree,
        centerY + position.yDegrees * frame.pixelsPerDegree,
        markerRadius,
        distractor.symbol,
      );
    }
  } else if (frame.elapsed < stimulus.presentationMs + stimulus.maskMs) {
    renderStaticMask(context, frame.width, frame.height, trial.seed);
  } else {
    drawLandscape(context, frame.width, frame.height, trial.seed, stimulus.backgroundComplexity);
    context.fillStyle = 'rgba(10, 19, 16, 0.44)';
    context.fillRect(0, 0, frame.width, frame.height);
  }
  context.restore();
}

function vehicleVisual(type: CentralVehicleType): ResponseVisual {
  return `vehicle-${type}`;
}

function centralOptions(stimulus: CentralPeripheralStimulus): readonly VisualResponseOption[] {
  return stimulus.vehicleChoices.map((vehicle, index) => ({
    value: `vehicle:${vehicle}`,
    keys: [String(index + 1)],
    keyLabel: String(index + 1),
    label: VEHICLE_LABELS[vehicle],
    visual: vehicleVisual(vehicle),
  }));
}

function positionOptions(): readonly VisualResponseOption[] {
  return POSITION_LABELS.map((label, index) => {
    const position = (index + 1) as PeripheralPosition;
    return {
      value: `location:${position}`,
      keys: [String(position)],
      keyLabel: String(position),
      label,
      visual: 'location',
      position: peripheralPositionPercent(position),
    };
  });
}

export function adaptCentralPeripheral(current: number, recentResults: TrialResult[]): number {
  return adaptDifficultyToPerformance(current, recentResults);
}

export const centralPeripheralModule: RuntimeTrainingModule<CentralPeripheralStimulus> = {
  id: 'central-peripheral',
  label: '車種と周辺標識を同時に見分ける',
  shortLabel: '中央・周辺の同時判断',
  protocolVersion: PROTOCOL_VERSION,
  evidenceLabel: '中央・周辺の二重判断課題',
  instructions: [
    '中央の車種と、周辺に一瞬出る白い標識の位置を同時に覚えます。記号は黒い丸かばつです。',
    '刺激とマスクが消えたら、まず車種ボタン、次に黒い丸の標識があった画面位置をクリックします。',
    '正答すると提示が短くなり、標識が遠く・背景が複雑になり、ばつの妨害標識が増えます。',
  ],
  responseKind: 'multi-select',
  selectionCount: 2,
  autoSubmitMultiSelect: true,
  serializeSelections(selected): string {
    return selected.join(',');
  },
  createTrial(seed: number, difficulty: number, mode: SessionMode): TrialPlan<CentralPeripheralStimulus> {
    const boundedDifficulty = clampDifficulty(difficulty, 1, 5);
    const stimulus = createCentralPeripheralStimulus(seed, boundedDifficulty);
    return {
      id: `central-peripheral-${mode}-${seed}`,
      moduleId: 'central-peripheral',
      protocolVersion: PROTOCOL_VERSION,
      seed,
      stimulusId: `dd-${stimulus.centralVehicle}-circle-${stimulus.peripheralPosition}-${stimulus.presentationMs}-${seed}`,
      stimulus,
      correctResponse: `vehicle:${stimulus.centralVehicle},location:${stimulus.peripheralPosition}`,
      timings: {
        fixationMs: 500,
        stimulusMs: stimulus.presentationMs + stimulus.maskMs,
        responseWindowMs: 8_000,
      },
      difficulty: boundedDifficulty,
    };
  },
  render: renderCentralPeripheral,
  isResponseAllowed(response: string): boolean {
    return /^vehicle:(convertible|box-truck|sedan|suv|hatchback),location:[1-8]$/.test(response);
  },
  adaptDifficulty: adaptCentralPeripheral,
  getResponseOptions(trial, selected = []): readonly ResponseOption[] {
    return selected.length === 0 ? centralOptions(trial.stimulus) : positionOptions();
  },
  getResponsePrompt(_trial, selected): string {
    return selected.length === 0 ? '中央に出た車は？' : '黒い丸の標識はどこ？';
  },
  getFeedback(trial, response, correct): ModuleFeedback {
    const position = POSITION_LABELS[trial.stimulus.peripheralPosition - 1];
    return {
      title: correct ? '車種と位置が一致しました' : '中央と周辺を確認してください',
      detail: response ? '車種と標識位置の二段階回答を記録しました。' : '制限時間内に2つ回答できませんでした。',
      correctLabel: `${VEHICLE_LABELS[trial.stimulus.centralVehicle]} / 黒い丸 / ${trial.stimulus.peripheralPosition} ${position}`,
    };
  },
  getResultMetadata(trial, response) {
    const selected = new Set(response?.split(',') ?? []);
    return {
      eccentricityDegrees: trial.stimulus.eccentricityDegrees,
      peripheralPosition: trial.stimulus.peripheralPosition,
      targetSignSymbol: trial.stimulus.targetSignSymbol,
      presentationDurationMs: trial.stimulus.presentationMs,
      backgroundComplexity: trial.stimulus.backgroundComplexity,
      distractorCount: trial.stimulus.distractorCount,
      vehicleSimilarity: trial.stimulus.vehicleSimilarity,
      vehiclePair: trial.stimulus.vehicleChoices.join('/'),
      centralCorrect: selected.has(`vehicle:${trial.stimulus.centralVehicle}`),
      peripheralCorrect: selected.has(`location:${trial.stimulus.peripheralPosition}`),
    };
  },
  getThreshold(results) {
    const isFixedAssessment = results.some(
      (result) => result.moduleId === 'central-peripheral' && result.metadata.assessment === true,
    );
    const fixed = estimateFixedFormThreshold(results, {
      moduleId: 'central-peripheral',
      metadataKey: 'presentationDurationMs',
      direction: 'increasing',
    });
    if (fixed !== undefined) return { value: fixed, unit: 'ms' };
    if (isFixedAssessment) return undefined;
    const value = estimateReversalThreshold(results, {
      moduleId: 'central-peripheral',
      metadataKey: 'presentationDurationMs',
      lastReversals: 6,
    });
    return value === undefined ? undefined : { value, unit: 'ms' };
  },
};
